import { useState, useRef, useEffect } from "react";
import { IconCalendar, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { clsx } from "clsx";
import { Text } from "../typography";
import { Button } from "../button";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDisplay(value: string): string {
  const [y, m, d] = value.split("-");
  return `${m}/${d}/${y}`;
}

function buildGrid(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();

  const cells: { day: number; outside: boolean; dateStr: string }[] = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, outside: true, dateStr: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, outside: false, dateStr: `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    cells.push({ day: d, outside: true, dateStr: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
  }
  return cells;
}

export interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  min?: string;
  /** `md` (default, h-10) matches compact form controls; `lg` (h-[54px]) matches the default `<Input>` height. */
  size?: "md" | "lg";
}

export function DatePicker({ value, onChange, label, min, size = "md" }: DatePickerProps) {
  const initialDate = value ? new Date(value + "T12:00:00") : new Date();
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());
  const wrapperRef = useRef<HTMLDivElement>(null);
  const today = toYMD(new Date());

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }

  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  function selectDay(dateStr: string) {
    if (min && dateStr < min) return;
    onChange(dateStr);
  }

  function selectToday() {
    onChange(today);
    const t = new Date();
    setViewYear(t.getFullYear());
    setViewMonth(t.getMonth());
  }

  const cells = buildGrid(viewYear, viewMonth);

  return (
    <div className="flex flex-col gap-1.5" ref={wrapperRef}>
      {label && (
        <button type="button" className="px-1.5 text-left" onClick={() => setOpen(v => !v)}>
          <Text variant="bodySmBold" tone="body" inline>{label}</Text>
        </button>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className={clsx(
            "flex w-full items-center gap-2 rounded-2xl border border-borderDefault bg-bgWhite px-4 cursor-pointer shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]",
            size === "lg" ? "h-[54px]" : "h-10",
          )}
        >
          <IconCalendar size={16} className="shrink-0 text-textBody" />
          <Text variant="bodySmBold" tone="dark" inline>
            {value ? formatDisplay(value) : "Select date"}
          </Text>
        </button>

        {open && (
          <div className="absolute top-12 left-0 z-50 w-[380px] rounded-2xl bg-bgWhite border border-borderLight shadow-[0px_8px_32px_rgba(0,0,0,0.1)] p-5">
            <div className="flex items-center justify-between mb-5">
              <button
                type="button"
                onClick={prevMonth}
                className="flex items-center justify-center w-8 h-8 rounded-xl bg-bgLight text-textBody hover:text-textDark transition-colors"
              >
                <IconChevronLeft size={16} />
              </button>
              <Text variant="bodyBold" tone="dark">
                {MONTH_NAMES[viewMonth]} {viewYear}
              </Text>
              <button
                type="button"
                onClick={nextMonth}
                className="flex items-center justify-center w-8 h-8 rounded-xl bg-bgLight text-textBody hover:text-textDark transition-colors"
              >
                <IconChevronRight size={16} />
              </button>
            </div>

            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map((d, i) => (
                <div key={i} className="flex items-center justify-center h-8">
                  <Text variant="bodySm" tone="muted" inline>{d}</Text>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {cells.map((cell, i) => {
                const isSelected = cell.dateStr === value;
                const isToday = cell.dateStr === today;
                const isDisabled = !!min && cell.dateStr < min;

                return (
                  <div key={i} className="flex items-center justify-center">
                    <button
                      type="button"
                      disabled={isDisabled}
                      onClick={() => selectDay(cell.dateStr)}
                      className={clsx(
                        "w-9 h-9 flex items-center justify-center rounded-2xl transition-colors",
                        isSelected && "bg-textDark text-bgWhite",
                        !isSelected && isToday && "text-accentCyan",
                        !isSelected && !isToday && cell.outside && "text-textMuted",
                        !isSelected && !isToday && !cell.outside && "text-textDark",
                        !isSelected && !isDisabled && "hover:bg-bgLight",
                        isDisabled && "cursor-not-allowed opacity-30",
                      )}
                    >
                      <Text variant="body" inline tone="inherit">
                        {cell.day}
                      </Text>
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between mt-4 pt-3 border-t border-borderLight">
              <button type="button" onClick={selectToday}>
                <Text variant="bodySm" tone="accent" inline>Today</Text>
              </button>
              <Button variant="filledDark" size="sm" onClick={() => setOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
